import { buildMkSourceConfig } from '@app/shared/utils/streaming-url.utils';
import { startDrmDiagnostics } from '@app/shared/utils/drm-diagnostics.utils';

declare var mkplayer: any;

export const MKPLAYER_CONFIG_BASE = {
  key: 'd0167b1c-9767-4287-9ddc-e0fa09d31e02',
  playback: { muted: true, autoplay: false },
  ui: false,
};

/** プレイヤーロードタイムアウト (ms) */
export const PLAYER_LOAD_TIMEOUT_MS = 8_000;
/** リトライ間隔 (ms) */
export const PLAYER_RETRY_INTERVAL_MS = 10_000;
/** 最大リトライ回数 */
export const PLAYER_RETRY_MAX = 18; // 3分

export interface MkplayerLifecycleCallbacks {
  onError?: (event?: any) => void;
  /** `<video>` 要素が DOM に挿入された時点（再生可能とは限らない・診断用）。 */
  onVideoElement?: (video: HTMLVideoElement) => void;
  /**
   * `<video>` が実際に再生可能になった時点（duration>0 または readyState>=HAVE_CURRENT_DATA）。
   * リトライタイマーの停止はこちらを基準にする（DRMで video は挿入されたが再生不能なまま
   * 固まるケースで、誤って「ロード成功」と判定しないため）。
   */
  onReady?: (video: HTMLVideoElement) => void;
}

/** `<video>` が再生可能（duration>0 または readyState>=HAVE_CURRENT_DATA(2)）か。 */
export function isVideoPlayable(video: HTMLVideoElement): boolean {
  return (Number.isFinite(video.duration) && video.duration > 0) || video.readyState >= 2;
}

/**
 * `<video>` が実際に再生可能になったタイミングで一度だけ `onReady` を呼ぶ。
 * 既に再生可能なら同期的に呼ぶ。返り値は購読解除関数（多重発火・リーク防止）。
 *
 * `<video>` 挿入だけでは「ロード成功」とみなさないための判定をここに集約する。
 */
export function watchVideoPlayable(
  video: HTMLVideoElement,
  onReady: (video: HTMLVideoElement) => void,
): () => void {
  if (isVideoPlayable(video)) {
    onReady(video);
    return () => { /* noop */ };
  }
  let fired = false;
  const readyEvents = ['loadedmetadata', 'durationchange', 'loadeddata', 'canplay'];
  const cleanup = (): void => {
    for (const ev of readyEvents) video.removeEventListener(ev, handler);
  };
  const handler = (): void => {
    if (fired || !isVideoPlayable(video)) return;
    fired = true;
    cleanup();
    onReady(video);
  };
  for (const ev of readyEvents) video.addEventListener(ev, handler);
  return cleanup;
}

export function isTransientPlaybackStartWarning(error: any): boolean {
  const code = error?.code ?? error?.data?.code;
  const name = String(error?.name ?? error?.data?.name ?? '');
  const message = String(error?.message ?? error?.data?.message ?? '');
  return (
    code === 1303 ||
    name.includes('PLAYBACK_COULD_NOT_BE_STARTED') ||
    message.includes('PLAYBACK_COULD_NOT_BE_STARTED')
  );
}

/**
 * MKPlayer インスタンスを作成し、DRM 付きストリーミングをロードする。
 * manifest URL のブラウザ別書き換えと DRM 設定は buildMkSourceConfig に集約。
 */
export function createMkplayerInstance(
  el: HTMLElement,
  streamingURL: string,
  streamingToken: string,
  callbacksOrError?: MkplayerLifecycleCallbacks | (() => void),
): any {
  let destroying = false;
  let videoObserver: MutationObserver | null = null;
  let fallbackVideoTimer: ReturnType<typeof setTimeout> | null = null;
  const readyCleanups: (() => void)[] = [];
  const callbacks: MkplayerLifecycleCallbacks =
    typeof callbacksOrError === 'function'
      ? { onError: callbacksOrError }
      : callbacksOrError ?? {};

  // ブラウザに合致する単一ソース（hls または dash）+ DRM 設定を生成。
  // 以前の hls/dash 両渡しは encryption 不整合 URL の試行を招くため廃止。
  const { sourceConfig, profile, url } = buildMkSourceConfig(streamingURL, streamingToken);
  const diag = startDrmDiagnostics('user-media', url, profile);

  const config = {
    ...MKPLAYER_CONFIG_BASE,
    events: {
      [mkplayer.MKPlayerEvent.Error]: (event: any) => {
        if (destroying) return;
        diag.onError(event);
        if (isTransientPlaybackStartWarning(event)) {
          return;
        }
        callbacks.onError?.(event);
      },
      [mkplayer.MKPlayerEvent.DrmLicenseAdded]: (event: any) => {
        if (destroying) return;
        diag.onLicenseAdded(event);
      },
    },
  };

  const instance = new mkplayer.MKPlayer(el, config);
  diag.onPlayerCreated(instance);

  const loadResult = instance.load(sourceConfig);
  Promise.resolve(loadResult)
    .then(() => {
      if (destroying) return;
      try { instance.preload?.(); } catch { /* ignore preload support/runtime errors */ }
    })
    .catch(() => {
      // MKPlayer also emits Error events; avoid an unhandled load rejection here.
    });

  // <video> 要素は MKPlayer がホスト配下に非同期で挿入する。診断用に検出して登録する。
  const registerVideo = (video: HTMLVideoElement): void => {
    if (destroying) return;
    if (fallbackVideoTimer) {
      clearTimeout(fallbackVideoTimer);
      fallbackVideoTimer = null;
    }
    diag.onVideoElement(video);
    callbacks.onVideoElement?.(video);
    // 「実際に再生可能」になったら onReady を発火（<video> 挿入だけでは止めない）。
    if (callbacks.onReady) {
      readyCleanups.push(
        watchVideoPlayable(video, (v) => {
          if (!destroying) callbacks.onReady?.(v);
        }),
      );
    }
  };
  const existing = el.querySelector('video') as HTMLVideoElement | null;
  if (existing) {
    registerVideo(existing);
  } else {
    videoObserver = new MutationObserver((mutations) => {
      if (destroying) {
        videoObserver?.disconnect();
        return;
      }
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (node instanceof HTMLVideoElement) {
            videoObserver?.disconnect();
            registerVideo(node);
            return;
          }
          if (node instanceof HTMLElement) {
            const video = node.querySelector('video') as HTMLVideoElement | null;
            if (video) {
              videoObserver?.disconnect();
              registerVideo(video);
              return;
            }
          }
        }
      }
    });
    videoObserver.observe(el, { childList: true, subtree: true });
    fallbackVideoTimer = setTimeout(() => {
      if (destroying) return;
      const video = el.querySelector('video') as HTMLVideoElement | null;
      if (video) {
        videoObserver?.disconnect();
        registerVideo(video);
      }
    }, PLAYER_LOAD_TIMEOUT_MS);
  }

  const originalDestroy = instance.destroy?.bind(instance);
  instance.destroy = () => {
    destroying = true;
    videoObserver?.disconnect();
    for (const cleanup of readyCleanups.splice(0)) cleanup();
    if (fallbackVideoTimer) {
      clearTimeout(fallbackVideoTimer);
      fallbackVideoTimer = null;
    }
    try {
      const result = originalDestroy?.();
      result?.catch?.(() => { /* suppress async DOM removal errors */ });
      return result;
    } catch {
      /* suppress sync errors */
    }
  };

  return instance;
}

/**
 * MKPlayer の destroy 時に出る console.error を抑制する ref-count 方式のヘルパー。
 */
let suppressRefCount = 0;
let origConsoleError: ((...args: any[]) => void) | null = null;

export function beginSuppressMkplayerErrors(): void {
  if (suppressRefCount === 0) {
    origConsoleError = console.error;
    console.error = (...args: unknown[]) => {
      const msg = String(args[0] ?? '');
      if (msg.includes('PlayerError') || msg.includes('1204') || msg.includes('SRC_NOT_SUPPORTED')) return;
      origConsoleError!.apply(console, args);
    };
  }
  suppressRefCount++;
}

export function endSuppressMkplayerErrors(): void {
  suppressRefCount--;
  if (suppressRefCount <= 0) {
    suppressRefCount = 0;
    if (origConsoleError) {
      console.error = origConsoleError;
      origConsoleError = null;
    }
  }
}

/**
 * MKPlayer インスタンスを安全に破棄する。
 */
export function destroyMkplayerInstance(instance: any): void {
  if (!instance) return;
  beginSuppressMkplayerErrors();
  try { instance.destroy()?.catch?.(() => {}); } catch { /* noop */ }
  setTimeout(() => endSuppressMkplayerErrors(), 500);
}
